import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/utils/constants';

/**
 * Bright amber banner shown across the top of every page when the admin has
 * enabled "Cleanup Mode" — a global toggle that suppresses automatic customer
 * emails (e.g. the Not-Proceeding collection notice) so an operator can tidy
 * up old / migrated jobs without surprising customers with a 2nd email.
 *
 * Clicking the banner disables Cleanup Mode immediately. Manual button-
 * triggered emails are not affected by the flag; only the ones the app fires
 * automatically in response to status / approval / signature events.
 */
export const CleanupModeBanner = () => {
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setActive(!!data?.cleanup_mode_active);
    } catch (_) {
      /* silently ignore — banner just stays hidden */
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000); // refresh every 30s in case toggled in another tab
    return () => clearInterval(id);
  }, []);

  if (!active) return null;

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="cleanup-mode-banner"
      title="Click to disable Cleanup Mode and resume automatic emails"
      onClick={async () => {
        if (busy) return;
        setBusy(true);
        try {
          await axios.put(`${API}/settings`, { cleanup_mode_active: false });
          setActive(false);
        } finally {
          setBusy(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click();
      }}
      style={{
        position: 'fixed',
        top: '0.5rem',
        left: '0.75rem',
        zIndex: 9998, // just below the env-badge
        backgroundColor: '#f59e0b',
        color: '#1f2937',
        padding: '0.3rem 0.65rem',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        border: '1px solid #b45309',
        borderRadius: '0.375rem',
        userSelect: 'none',
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        fontFamily: 'system-ui, sans-serif',
        maxWidth: 'fit-content',
      }}
    >
      ⚠️ Cleanup Mode ON — click to disable
    </div>
  );
};
